import { MultiDocumentPreview } from '@Components/Documents';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { usePrintDocument } from '@Hooks/Documents';
import { type ApiPrintOption } from '@Model';
import {
    Button,
    Card,
    defaultIconSize,
    FlexRow,
    getSpacings,
    Modal,
    type Spacing,
} from '@synqo/frontend-component-library';
import { IconDownload, IconMailPlus, IconPrinter } from '@tabler/icons-react';

export interface PrintOptionsModalProps {
    printOption?: ApiPrintOption;
    onClose: () => void;
    sendAsEmail?: () => void;
}

const footerCardSpacings = getSpacings<Spacing>({ bottom: 'xxs' }, 'xs');
const previewCardSpacings = getSpacings<Spacing>('xs');

export function PrintOptionsModal({ printOption, onClose, sendAsEmail }: PrintOptionsModalProps) {
    const printDocument = usePrintDocument();
    const canSendEmail = useHasPermission()(Permission.MailingEmailsCreate);

    if (!printOption) {
        return undefined;
    }

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title="Vorschau"
            footer={
                <Card name="print_preview_footer" border="withTopSeparator" padding={footerCardSpacings}>
                    <FlexRow>
                        <Button
                            key="btnDownload"
                            onClick={() => {
                                printDocument({
                                    printOption: printOption.id,
                                    primaryId: printOption.related_object_id,
                                    mode: 'download',
                                });
                            }}
                        >
                            <IconDownload size={defaultIconSize} />
                            &nbsp;PDF Herunterladen
                        </Button>
                        <Button
                            key="btnPrint"
                            onClick={() => {
                                printDocument({
                                    printOption: printOption.id,
                                    primaryId: printOption.related_object_id,
                                    mode: 'print',
                                });
                            }}
                        >
                            <IconPrinter size={defaultIconSize} />
                            &nbsp;PDF Drucken
                        </Button>
                        {sendAsEmail && canSendEmail && (
                            <>
                                <FlexRow maximize />
                                <Button
                                    key="btnSendEmail"
                                    onClick={() => {
                                        onClose();
                                        sendAsEmail();
                                    }}
                                >
                                    <IconMailPlus size={defaultIconSize} />
                                    &nbsp;Als Email versenden
                                </Button>
                            </>
                        )}
                    </FlexRow>
                </Card>
            }
        >
            <Card name="print_preview" border="withoutBorder" padding={previewCardSpacings}>
                <MultiDocumentPreview
                    printOption={printOption}
                    primaryId={printOption.related_object_id}
                    onError={onClose}
                />
            </Card>
        </Modal>
    );
}
